import React from "react";

// Dashboard
const RegistryDashboard = React.lazy(() => import("../../Registry/RegistryDashboard"));

// Vendors
const RegistryVendors = React.lazy(() => import("../../Registry/Vendors/RegistryVendors"));
const AddRegistryVendorForm = React.lazy(
  () => import("../../Registry/Vendors/AddRegistryVendorForm"),
);

// Charge Heads
const RegistryChargeHeads = React.lazy(
  () => import("../../Registry/ChargeHeads/RegistryChargeHeads"),
);
const AddRegistryChargeHeadForm = React.lazy(
  () => import("../../Registry/ChargeHeads/AddRegistryChargeHeadForm"),
);

// Chart of Accounts
const RegistryChartOfAccounts = React.lazy(
  () => import("../../Registry/ChartOfAccounts/RegistryChartOfAccounts"),
);

// General vouchers and account creation — the Accounts module's own screens, pointed
// at the Registry project. See RegistryAccountsScreens for why they take props.
const RegistryAccountsScreens = () => import("../../Registry/Accounts/RegistryAccountsScreens");
const RegistryGeneralReceiptTable = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryGeneralReceiptTable })),
);
const RegistryGeneralReceiptForm = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryGeneralReceiptForm })),
);
const RegistryGeneralPaymentTable = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryGeneralPaymentTable })),
);
const RegistryGeneralPaymentForm = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryGeneralPaymentForm })),
);
const RegistryJournalEntryTable = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryJournalEntryTable })),
);
const RegistryJournalEntryForm = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryJournalEntryForm })),
);
const RegistryAddAccount = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryAddAccount })),
);
const RegistryAddHeading = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryAddHeading })),
);
const RegistryBanks = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryBanks })),
);
const RegistryBankForm = React.lazy(() =>
  RegistryAccountsScreens().then((m) => ({ default: m.RegistryBankForm })),
);

// Cases
const RegistryCases = React.lazy(() => import("../../Registry/Cases/RegistryCases"));
const ShiftSaleToRegistry = React.lazy(() => import("../../Registry/Cases/ShiftSaleToRegistry"));

// Receipts
const RegistryReceiptTable = React.lazy(
  () => import("../../Registry/Receipts/RegistryReceiptTable"),
);
const RegistryReceiptForm = React.lazy(() => import("../../Registry/Receipts/RegistryReceiptForm"));

// Payments
const RegistryPaymentTable = React.lazy(
  () => import("../../Registry/Payments/RegistryPaymentTable"),
);
const RegistryPaymentForm = React.lazy(() => import("../../Registry/Payments/RegistryPaymentForm"));

// Reports — each is the Accounts module's report pinned to the Registry project
const RegistryTrialBalance = React.lazy(
  () => import("../../Registry/Reports/RegistryTrialBalance"),
);
const RegistryBalanceSheet = React.lazy(
  () => import("../../Registry/Reports/RegistryBalanceSheet"),
);
const RegistryIncomeStatement = React.lazy(
  () => import("../../Registry/Reports/RegistryIncomeStatement"),
);
const RegistryCashBook = React.lazy(() => import("../../Registry/Reports/RegistryCashBook"));
const RegistryBankBook = React.lazy(() => import("../../Registry/Reports/RegistryBankBook"));
const RegistryGeneralLedger = React.lazy(
  () => import("../../Registry/Reports/RegistryGeneralLedger"),
);
// History snapshots — Registry's own reports, not the standard financial statements
const RegistryUnitHistory = React.lazy(() => import("../../Registry/Reports/RegistryUnitHistory"));
const RegistryProjectHistory = React.lazy(
  () => import("../../Registry/Reports/RegistryProjectHistory"),
);
const RegistryCustomerHistory = React.lazy(
  () => import("../../Registry/Reports/RegistryCustomerHistory"),
);

// Settings — Process Stages (Charge Heads route is defined above, just relocated
// in the sidebar under Settings)
const RegistryProcessStages = React.lazy(
  () => import("../../Registry/Stages/RegistryProcessStages"),
);

// rolename values match screen.role_name seeded by
// erp-backend/Gravity_Solutions/Migrations/SeedRegistryScreens.sql
export const registryRoutes = [
  {
    path: "/registry/dashboard",
    Component: RegistryDashboard,
    rolename: "Registry",
  },

  // Vendors
  {
    path: "/registry/vendors",
    Component: RegistryVendors,
    rolename: "Registry Vendors_View",
  },
  {
    path: "/registry/vendors/add",
    Component: AddRegistryVendorForm,
    rolename: "Registry Vendors_Create",
  },
  {
    path: "/registry/vendors/edit",
    Component: AddRegistryVendorForm,
    rolename: "Registry Vendors_Edit",
  },

  // Charge Heads
  {
    path: "/registry/charge-heads",
    Component: RegistryChargeHeads,
    rolename: "Registry Charge Heads_View",
  },
  {
    path: "/registry/charge-heads/add",
    Component: AddRegistryChargeHeadForm,
    rolename: "Registry Charge Heads_Create",
  },

  // Chart of Accounts — has its own screen now, so it can be granted and taken away
  // like every other entry in the menu rather than riding on module access.
  // Path deliberately avoids the substring "account": App.js picks the sidebar array
  // by checking path.includes("account") before path.includes("registry"), so any
  // /registry/* path containing "account" would show the Accounts sidebar instead.
  // Sibling sidebar routes are matched by substring (Layout.js), so no route may be a
  // prefix of another — hence coa/chart and coa/add rather than coa and coa/add.
  // /registry/coa is kept as an alias so existing links still resolve.
  {
    path: "/registry/coa",
    Component: RegistryChartOfAccounts,
    rolename: "Registry Chart Of Account_View",
  },
  {
    path: "/registry/coa/chart",
    Component: RegistryChartOfAccounts,
    rolename: "Registry Chart Of Account_View",
  },

  // Cases
  {
    path: "/registry/cases",
    Component: RegistryCases,
    rolename: "Registry Cases_View",
  },
  {
    path: "/registry/cases/shift",
    Component: ShiftSaleToRegistry,
    rolename: "Registry Cases_Create",
  },

  // Receipts — Case Receipt draws on a case's charge heads; General Receipt is the
  // Accounts module's own voucher, posted into the Registry project.
  // /registry/receipts and /registry/receipts/add are aliases of the case screens,
  // kept so older links keep working; the sidebar points at the /case paths.
  {
    path: "/registry/receipts",
    Component: RegistryReceiptTable,
    rolename: "Registry Receipt_View",
  },
  {
    path: "/registry/receipts/add",
    Component: RegistryReceiptForm,
    rolename: "Registry Receipt_Create",
  },
  {
    path: "/registry/receipts/case",
    Component: RegistryReceiptTable,
    rolename: "Registry Receipt_View",
  },
  {
    path: "/registry/receipts/case/add",
    Component: RegistryReceiptForm,
    rolename: "Registry Receipt_Create",
  },
  {
    path: "/registry/receipts/general",
    Component: RegistryGeneralReceiptTable,
    rolename: "Registry General Receipt_View",
  },
  {
    path: "/registry/receipts/general/add",
    Component: RegistryGeneralReceiptForm,
    rolename: "Registry General Receipt_Create",
  },

  // Payments
  {
    path: "/registry/payments",
    Component: RegistryPaymentTable,
    rolename: "Registry Payment_View",
  },
  {
    path: "/registry/payments/add",
    Component: RegistryPaymentForm,
    rolename: "Registry Payment_Create",
  },
  {
    path: "/registry/payments/case",
    Component: RegistryPaymentTable,
    rolename: "Registry Payment_View",
  },
  {
    path: "/registry/payments/case/add",
    Component: RegistryPaymentForm,
    rolename: "Registry Payment_Create",
  },
  {
    path: "/registry/payments/general",
    Component: RegistryGeneralPaymentTable,
    rolename: "Registry General Payment_View",
  },
  {
    path: "/registry/payments/general/add",
    Component: RegistryGeneralPaymentForm,
    rolename: "Registry General Payment_Create",
  },

  // Accounts — account creation and the journal entry voucher. Paths use "coa"
  // rather than "accounts" for the sidebar reason noted above.
  {
    path: "/registry/coa/add",
    Component: RegistryAddAccount,
    rolename: "Registry Accounts_Create",
  },
  // Same role as adding an account: this replaced the heading modal inside it.
  {
    path: "/registry/coa/heading",
    Component: RegistryAddHeading,
    rolename: "Registry Accounts_Create",
  },
  // "coa/banks" shares no prefix with coa/add or coa/chart, so the sidebar substring
  // match lights exactly one tab; the add form sits underneath it deliberately, so
  // Banks stays the active tab while adding.
  {
    path: "/registry/coa/banks",
    Component: RegistryBanks,
    rolename: "Registry Banks_View",
  },
  {
    path: "/registry/coa/banks/add",
    Component: RegistryBankForm,
    rolename: "Registry Banks_Create",
  },
  {
    path: "/registry/coa/journal-entry",
    Component: RegistryJournalEntryTable,
    rolename: "Registry Journal Entry_View",
  },
  {
    path: "/registry/coa/journal-entry/add",
    Component: RegistryJournalEntryForm,
    rolename: "Registry Journal Entry_Create",
  },

  // Reports
  {
    path: "/registry/reports/trial-balance",
    Component: RegistryTrialBalance,
    rolename: "Registry Reports_Trial Balance",
  },
  {
    path: "/registry/reports/balance-sheet",
    Component: RegistryBalanceSheet,
    rolename: "Registry Reports_Balance Sheet",
  },
  {
    path: "/registry/reports/income-statement",
    Component: RegistryIncomeStatement,
    rolename: "Registry Reports_Income Statement",
  },
  {
    path: "/registry/reports/cash-book",
    Component: RegistryCashBook,
    rolename: "Registry Reports_Cash Book",
  },
  {
    path: "/registry/reports/bank-book",
    Component: RegistryBankBook,
    rolename: "Registry Reports_Bank Book",
  },
  {
    path: "/registry/reports/ledger",
    Component: RegistryGeneralLedger,
    rolename: "Registry Reports_Ledger",
  },
  {
    path: "/registry/reports/project-history",
    Component: RegistryProjectHistory,
    rolename: "Registry Reports_Project History",
  },
  {
    path: "/registry/reports/unit-history",
    Component: RegistryUnitHistory,
    rolename: "Registry Reports_Unit History",
  },
  {
    path: "/registry/reports/customer-history",
    Component: RegistryCustomerHistory,
    rolename: "Registry Reports_Customer History",
  },

  // Settings — Process Stages
  {
    path: "/registry/process-stages",
    Component: RegistryProcessStages,
    rolename: "Registry Process Stages_View",
  },
];
